/**
 * components/icon.tsx
 * ──────────────────────────────────────────────────────────────────────────
 * Pixel-faithful icon set. Each icon is a small shape spec (the EXACT
 * coordinates used in DESIGN_INSTRUCTIONS/SK Upsell.dc.html), rendered once
 * for web (inline <svg>) and once for native (react-native-svg) from the same
 * spec — zero duplication. Stroke icons default to strokeWidth 2 (the design's
 * weight); filled icons (star/sparkle badge) set `filled`.
 * ──────────────────────────────────────────────────────────────────────────
 */
import React from 'react';
import { Platform } from 'react-native';

type Shape =
  | { t: 'path'; d: string }
  | { t: 'line'; x1: number; y1: number; x2: number; y2: number }
  | { t: 'circle'; cx: number; cy: number; r: number }
  | { t: 'polyline'; points: string }
  | { t: 'rect'; x: number; y: number; w: number; h: number; rx?: number };

interface IconDef {
  shapes: Shape[];
  filled?: boolean;
}

const I = (shapes: Shape[]): IconDef => ({ shapes });
const F = (shapes: Shape[]): IconDef => ({ shapes, filled: true });

const ICONS = {
  // ── Sidebar nav ──────────────────────────────────────────────────────────
  'rules-list': I([
    { t: 'line', x1: 8, y1: 6, x2: 21, y2: 6 },
    { t: 'line', x1: 8, y1: 12, x2: 21, y2: 12 },
    { t: 'line', x1: 8, y1: 18, x2: 21, y2: 18 },
    { t: 'circle', cx: 3.5, cy: 6, r: 1 },
    { t: 'circle', cx: 3.5, cy: 12, r: 1 },
    { t: 'circle', cx: 3.5, cy: 18, r: 1 },
  ]),
  'chart-line': I([
    { t: 'path', d: 'M3 3v18h18' },
    { t: 'path', d: 'M7 14l4-4 3 3 5-6' },
  ]),
  sparkle: I([
    { t: 'path', d: 'M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9z' },
    { t: 'path', d: 'M19 14l.7 1.8L21.5 16.5 19.7 17.2 19 19l-.7-1.8L16.5 16.5l1.8-.7z' },
  ]),
  pencil: I([
    { t: 'path', d: 'M12 20h9' },
    { t: 'path', d: 'M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z' },
  ]),
  gear: I([
    { t: 'circle', cx: 12, cy: 12, r: 3 },
    {
      t: 'path',
      d: 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z',
    },
  ]),
  // ── Actions ────────────────────────────────────────────────────────────────
  search: I([
    { t: 'circle', cx: 11, cy: 11, r: 7 },
    { t: 'line', x1: 21, y1: 21, x2: 16.65, y2: 16.65 },
  ]),
  plus: I([
    { t: 'line', x1: 12, y1: 5, x2: 12, y2: 19 },
    { t: 'line', x1: 5, y1: 12, x2: 19, y2: 12 },
  ]),
  check: I([{ t: 'polyline', points: '20 6 9 17 4 12' }]),
  refresh: I([
    { t: 'path', d: 'M21 2v6h-6' },
    { t: 'path', d: 'M3 12a9 9 0 0 1 15-6.7L21 8' },
    { t: 'path', d: 'M3 22v-6h6' },
    { t: 'path', d: 'M21 12a9 9 0 0 1-15 6.7L3 16' },
  ]),
  info: I([
    { t: 'circle', cx: 12, cy: 12, r: 10 },
    { t: 'path', d: 'M12 16v-4' },
    { t: 'path', d: 'M12 8h.01' },
  ]),
  'alert-circle': I([
    { t: 'circle', cx: 12, cy: 12, r: 10 },
    { t: 'line', x1: 12, y1: 8, x2: 12, y2: 12 },
    { t: 'line', x1: 12, y1: 16, x2: 12.01, y2: 16 },
  ]),
  'warn-tri': I([
    { t: 'path', d: 'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' },
    { t: 'line', x1: 12, y1: 9, x2: 12, y2: 13 },
    { t: 'line', x1: 12, y1: 17, x2: 12.01, y2: 17 },
  ]),
  'arrow-l': I([
    { t: 'line', x1: 19, y1: 12, x2: 5, y2: 12 },
    { t: 'polyline', points: '12 19 5 12 12 5' },
  ]),
  eye: I([
    { t: 'path', d: 'M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z' },
    { t: 'circle', cx: 12, cy: 12, r: 3 },
  ]),
  lock: I([
    { t: 'rect', x: 3, y: 11, w: 18, h: 11, rx: 2 },
    { t: 'path', d: 'M7 11V7a5 5 0 0 1 10 0v4' },
  ]),
  x: I([{ t: 'path', d: 'M18 6 6 18M6 6l12 12' }]),
  menu: I([
    { t: 'line', x1: 4, y1: 6, x2: 20, y2: 6 },
    { t: 'line', x1: 4, y1: 12, x2: 20, y2: 12 },
    { t: 'line', x1: 4, y1: 18, x2: 20, y2: 18 },
  ]),
  logout: I([
    { t: 'path', d: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' },
    { t: 'polyline', points: '16 17 21 12 16 7' },
    { t: 'line', x1: 21, y1: 12, x2: 9, y2: 12 },
  ]),
  // ── Filled ───────────────────────────────────────────────────────────────
  'star-fill': F([{ t: 'path', d: 'M12 2l2.4 6.9H22l-6 4.4 2.3 7.1L12 16.3 5.7 20.4 8 13.3 2 8.9h7.6z' }]),
  'sparkle-fill': F([{ t: 'path', d: 'M12 3l1.6 4L18 8.6l-4 1.6L12 14l-1.6-3.8L6 8.6l4-1.6z' }]),
} as const;

export type IconName = keyof typeof ICONS;

export interface IconProps {
  name: IconName;
  size?: number;
  color?: string;
  strokeWidth?: number;
  style?: React.CSSProperties;
}

const Icon: React.FC<IconProps> = ({ name, size = 16, color = 'currentColor', strokeWidth = 2, style }) => {
  const def = ICONS[name];
  const stroke = def.filled ? 'none' : color;
  const fill = def.filled ? color : 'none';

  if (Platform.OS === 'web') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={{ display: 'inline-block', flexShrink: 0, ...style }}
      >
        {def.shapes.map((s, i) => {
          switch (s.t) {
            case 'path':
              return <path key={i} d={s.d} />;
            case 'line':
              return <line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} />;
            case 'circle':
              return <circle key={i} cx={s.cx} cy={s.cy} r={s.r} />;
            case 'polyline':
              return <polyline key={i} points={s.points} />;
            case 'rect':
              return <rect key={i} x={s.x} y={s.y} width={s.w} height={s.h} rx={s.rx} />;
          }
        })}
      </svg>
    );
  }

  // Native
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const Svg = require('react-native-svg');
  const { default: SvgRoot, Path, Line, Circle, Polyline, Rect } = Svg;
  return (
    <SvgRoot
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {def.shapes.map((s, i) => {
        switch (s.t) {
          case 'path':
            return <Path key={i} d={s.d} />;
          case 'line':
            return <Line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} />;
          case 'circle':
            return <Circle key={i} cx={s.cx} cy={s.cy} r={s.r} />;
          case 'polyline':
            return <Polyline key={i} points={s.points} />;
          case 'rect':
            return <Rect key={i} x={s.x} y={s.y} width={s.w} height={s.h} rx={s.rx} />;
        }
      })}
    </SvgRoot>
  );
};

export default Icon;
